import React, { memo, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CustomEquipoWithTooltip from '@/DesignSystem/components/partidos/CustomEquipoWithTooltip';
import ComparisonBarComponent from '@/DesignSystem/features/comparison/ComparisonBar';
import { TeamStreak } from '@/components/clasificacion-competiciones/ClasificacionCompeticionesColumns';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import HomeIcon from '@/components/shared/icons/home-icon';
import AirPlaneIcon from '@/components/shared/icons/airplane-out-icon';

const metrics = [
    { key: 'victorias', label: 'Victorias', isHigherBetter: true },
    { key: 'derrotas', label: 'Derrotas', isHigherBetter: false },
    { key: 'empates', label: 'Empates', isHigherBetter: false },
    { key: 'golesFavor', label: 'Goles a favor', isHigherBetter: true },
    { key: 'golesContra', label: 'Goles en contra', isHigherBetter: false },
    { key: 'diferenciaGoles', label: 'Diferencia de goles', isHigherBetter: true },
    { key: 'puntos', label: 'Puntos', isHigherBetter: true },
];

const sides = [
    { label: 'Local', value: 'home', icon: <HomeIcon width={15} height={15} /> },
    { label: 'Visitante', value: 'away', icon: <AirPlaneIcon width={13} height={13} /> },
    { label: 'Total', value: 'total' },
];

const getMetricValue = (stats, key) => {
    if (!stats) return 0;

    switch (key) {
        case 'diferenciaGoles':
            return Number(stats.golesFavor ?? 0) - Number(stats.golesContra ?? 0);
        default:
            return Number(stats[key] ?? 0);
    }
};

const getPositionColor = (position) => {
    if (position >= 1 && position <= 5) return '#1976d2'; // Champions League
    if (position >= 6 && position <= 7) return '#388e3c'; // Europa League
    if (position === 8) return '#66bb6a'; // Conference League
    if (position >= 18 && position <= 20) return '#d32f2f'; // Descenso
    return '#202938'; // Zona media
};

const EquipoColumn = ({ equipo, sideValue, setSideValue, align = 'left', stats }) => {
    const isLeft = align === 'left';

    const isAvailableSide = (side) => {
        if (side === 'home') return stats?.home;
        if (side === 'away') return stats?.away;
        return stats?.total;
    };

    return (
        <Box display="flex" flexDirection="column" width="50%">
            <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                mb={1}
                justifyContent={isLeft ? 'flex-start' : 'flex-end'}
            >
                {isLeft && (
                    <CustomEquipoWithTooltip teamId={equipo.id} alt={equipo.name} width={35} height={35} />
                )}
                <Box textAlign={isLeft ? 'left' : 'right'}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        justifyContent={isLeft ? 'flex-start' : 'flex-end'}
                    >
                        {!isLeft && (
                            <Typography fontSize={12} noWrap>
                                {equipo.name}
                            </Typography>
                        )}
                        {equipo.stats?.position && (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize={12}
                                backgroundColor={getPositionColor(equipo.stats?.position)}
                                color="white"
                                width={15}
                                height={15}
                                borderRadius={0.5}
                                border={`1px solid`}
                                borderColor="border.main"
                            >
                                {equipo.stats?.position}
                            </Box>
                        )}
                        {isLeft && (
                            <Typography fontSize={12} noWrap>
                                {equipo.name}
                            </Typography>
                        )}
                    </Box>
                    <TeamStreak streak={stats?.form} />
                </Box>
                {!isLeft && (
                    <CustomEquipoWithTooltip teamId={equipo.id} width={35} height={35} alt={equipo.name} />
                )}
            </Box>
            <Box display="flex" gap={1.5} width="100%" justifyContent={isLeft ? 'flex-start' : 'flex-end'}>
                {sides.map((side) => {
                    const isAvailable = isAvailableSide(side.value);

                    return (
                        <Button
                            variant="text"
                            size="small"
                            fontSize={12}
                            key={side.value}
                            onClick={() => setSideValue(side.value)}
                            sx={{
                                cursor: 'pointer',
                                width: 'fit-content',
                                minWidth: 'auto',
                                padding: '0px 0px',
                                color: isAvailable ? 'text.primary' : 'text.disabled',
                                borderBottom: sideValue === side.value && isAvailable ? '3px solid' : 'none',
                                borderColor:
                                    sideValue === side.value && isAvailable ? 'primary.main' : 'transparent',
                                borderRadius: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            disabled={!isAvailable}
                        >
                            {side.icon ? side.icon : side.label}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};

const ComparacionEquipos = ({ home, away, stats }) => {
    const [homeAwayLeft, setHomeAwayLeft] = useState(stats?.home?.home ? 'home' : 'total');
    const [homeAwayRight, setHomeAwayRight] = useState(stats?.away?.away ? 'away' : 'total');

    const puntosTotales = useMemo(() => {
        return Number(stats?.home?.total?.puntos ?? 0) + Number(stats?.away?.total?.puntos ?? 0);
    }, [stats]);

    if (puntosTotales === 0) return null;

    return (
        <CustomSectionWrapper title="Comparación de equipos" color="accent2">
            <Box
                borderRadius={1}
                display="flex"
                alignItems="center"
                mb={1}
                px={1}
                py={0.5}
                borderBottom="1px solid"
                borderColor="border.main"
            >
                <EquipoColumn
                    equipo={home}
                    sideValue={homeAwayLeft}
                    setSideValue={setHomeAwayLeft}
                    align="left"
                    stats={stats?.home}
                />
                <EquipoColumn
                    equipo={away}
                    sideValue={homeAwayRight}
                    setSideValue={setHomeAwayRight}
                    align="right"
                    stats={stats?.away}
                />
            </Box>

            {metrics.map(({ key, label, isHigherBetter }) => {
                const homeValue = getMetricValue(stats?.home?.[homeAwayLeft], key);
                const awayValue = getMetricValue(stats?.away?.[homeAwayRight], key);
                const metricTotal = Math.max(homeValue, awayValue);

                return (
                    <ComparisonBarComponent
                        key={key}
                        parameter={label}
                        left={homeValue}
                        right={awayValue}
                        metricTotal={metricTotal}
                        isHigherBetter={isHigherBetter}
                    />
                );
            })}
        </CustomSectionWrapper>
    );
};

export default memo(ComparacionEquipos);
